import LegalPageLayout from '@/components/layout/LegalPageLayout';

const tocItems = [
  { id: 'acceptance', label: 'Acceptance of Terms' },
  { id: 'description', label: 'Description of Service' },
  { id: 'accounts', label: 'User Accounts' },
  { id: 'acceptable-use', label: 'Acceptable Use' },
  { id: 'billing', label: 'Subscription & Billing' },
  { id: 'ip', label: 'Intellectual Property' },
  { id: 'liability', label: 'Limitation of Liability' },
  { id: 'termination', label: 'Termination' },
  { id: 'changes', label: 'Changes to Terms' },
  { id: 'contact', label: 'Contact' },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="reveal py-10 border-b border-white/[0.04]">
      <h2 className="text-[22px] font-semibold tracking-tight leading-snug mb-5 text-white scroll-mt-[110px]">
        {title}
      </h2>
      <div className="text-sm text-zinc-500 leading-[1.75]">{children}</div>
    </section>
  );
}

function InfoCard({ children, accent }) {
  const border = accent ? 'border-white/[0.12]' : 'border-white/[0.06]';
  const bg = accent ? 'bg-white/[0.04]' : 'bg-white/[0.02]';
  return (
    <div className={`rounded-2xl border ${border} ${bg} p-5 my-5 transition-colors hover:border-white/[0.12]`}>
      {children}
    </div>
  );
}

export default function TermsPage() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      subtitle="By using ScheduleX, you agree to these terms. Please read them carefully."
      tocItems={tocItems}
    >
      <Section id="acceptance" title="1. Acceptance of Terms">
        <p>
          By accessing and using ScheduleX (&quot;the Service&quot;), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to abide by the above, please do not use this service.
        </p>
        <InfoCard>
          <p className="font-medium text-white mb-1">Age Requirement</p>
          <p>You must be at least 13 years old to use ScheduleX. If you are under 18, you must have permission from a parent or guardian.</p>
        </InfoCard>
      </Section>

      <Section id="description" title="2. Description of Service">
        <p>
          ScheduleX provides an online scheduling platform that allows users to create booking calendars, manage appointments, and coordinate meeting times. The Service is provided &quot;as is&quot; and we reserve the right to modify or discontinue the Service at any time.
        </p>
      </Section>

      <Section id="accounts" title="3. User Accounts">
        <p className="mb-4">
          To use certain features of the Service, you must register for an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
        </p>
        <InfoCard>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>You must provide accurate and complete information when creating an account</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>You agree to notify us immediately of any unauthorized use of your account</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>We reserve the right to suspend or terminate accounts that violate these terms</span>
            </li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="acceptable-use" title="4. Acceptable Use">
        <p className="mb-4">You agree not to use the Service to:</p>
        <InfoCard accent>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Upload, post, or transmit any content that is unlawful, harmful, or objectionable</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Impersonate any person or entity</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Engage in any activity that disrupts or interferes with the Service</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Attempt to gain unauthorized access to any portion of the Service</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Use the Service for any commercial spam activities</span>
            </li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="billing" title="5. Subscription & Billing">
        <p>
          ScheduleX offers both free and paid subscription plans. Paid subscriptions are billed in advance on a monthly basis. You may cancel your subscription at any time, and you will continue to have access to the paid features until the end of your billing period.
        </p>
        <InfoCard>
          <p className="font-medium text-white mb-1">Refund Policy</p>
          <p>We do not offer refunds for partial months. If you cancel, you retain access until the end of your current billing cycle.</p>
        </InfoCard>
      </Section>

      <Section id="ip" title="6. Intellectual Property">
        <p>
          The Service and its original content, features, and functionality are and will remain the exclusive property of ScheduleX and its licensors. You retain ownership of any content you create and upload to the Service.
        </p>
        <InfoCard>
          <p className="font-medium text-white mb-1">License</p>
          <p>By uploading content to ScheduleX, you grant us a non-exclusive, royalty-free license to use, display, and distribute that content solely for the purpose of operating the Service.</p>
        </InfoCard>
      </Section>

      <Section id="liability" title="7. Limitation of Liability">
        <p>
          In no event shall ScheduleX, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses.
        </p>
      </Section>

      <Section id="termination" title="8. Termination">
        <p>
          We may terminate or suspend your account and access to the Service immediately, without prior notice, for conduct that we believe violates these Terms of Service or is harmful to other users of the Service, us, or third parties, or for any other reason.
        </p>
        <InfoCard accent>
          <p className="font-medium text-white mb-1">Effect of Termination</p>
          <p>Upon termination, your right to use the Service will immediately cease. All provisions of the Terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.</p>
        </InfoCard>
      </Section>

      <Section id="changes" title="9. Changes to Terms">
        <p>
          We reserve the right to modify these terms at any time. We will always post the most current version on our website. By continuing to use the Service after changes are made, you agree to be bound by the revised terms.
        </p>
        <InfoCard>
          <p className="font-medium text-white mb-1">Notification</p>
          <p>For material changes, we will notify you via email or through a prominent notice on the Service at least 14 days before the changes take effect.</p>
        </InfoCard>
      </Section>

      <Section id="contact" title="10. Contact">
        <p>
          If you have any questions about these Terms, please contact us at{' '}
          <a href="mailto:support@schedulex.site" className="text-white hover:underline">
            support@schedulex.site
          </a>.
        </p>
      </Section>
    </LegalPageLayout>
  );
}
